// Pure, framework-free reveal calculation shared by the keyboard-aware
// focused-field reveal adapters (element-container and document/body). It is the
// single source of truth for how a focused field — or the union of a field and a
// companion action — is brought into a visible band, and for the minimal,
// clamped vertical correction required to do so.
//
// This module reads NOTHING: no DOM, no React, no viewport/keyboard state, no
// browser/Android/device signals. Callers measure the band, the field, the
// optional companion, and the current/maximum scroll offset in ONE consistent
// vertical coordinate space, and pass those numbers in. Both adapters therefore
// share identical field/companion geometry, minimal-correction, clamping and
// fallback rules, so they cannot drift apart. Horizontal position is outside this
// module's responsibility by construction — no horizontal value is accepted or
// returned.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

/** A closed vertical interval in the caller's coordinate space (CSS px). */
export interface VerticalInterval {
  readonly top: number;
  readonly bottom: number;
}

/** Everything the calculation needs, already measured by the caller. */
export interface RevealInput {
  /** The visible band the target must end up within (e.g. above the keyboard). */
  readonly band: VerticalInterval;
  /** The focused field's rectangle, in the same coordinate space as `band`. */
  readonly field: VerticalInterval;
  /**
   * Optional companion (e.g. a submit button) to keep visible together with the
   * field. Pass `null`/omit for field-only behaviour. The union is used only when
   * it fits the band; otherwise the calculation falls back to the field alone.
   */
  readonly companion?: VerticalInterval | null;
  /** The scroll owner's current vertical offset (px). */
  readonly scroll: number;
  /** The scroll owner's maximum vertical offset (px); corrections clamp to `[0, maxScroll]`. */
  readonly maxScroll: number;
}

/** The calculation's verdict — vertical only. */
export interface RevealResult {
  /** The vertical scroll offset to apply. Equals `input.scroll` when nothing should move. */
  readonly scroll: number;
  /** The clamped correction actually applied (`scroll - input.scroll`); `0` when unchanged. */
  readonly delta: number;
  /** `true` only when a correction is required (i.e. `scroll !== input.scroll`). */
  readonly changed: boolean;
}

/**
 * Compute the minimum vertical scroll correction that brings the reveal target
 * into the visible band, clamped to the owner's scroll range.
 *
 * Target selection:
 * - field alone, or the union of field + companion when that union fits the band;
 * - if the union is taller than the band, fall back to the field alone.
 *
 * Correction rules (minimum movement, in priority order):
 * - target taller than the band → expose its leading (top) edge (also the safe,
 *   predictable behaviour for a zero-sized or otherwise invalid band);
 * - target below the band → smallest downward correction;
 * - target above the band → smallest upward correction;
 * - target already fully visible → no correction.
 *
 * The correction is clamped to `[0, maxScroll]`; if the clamp cancels it out the
 * result reports `changed: false`. No horizontal value is read or returned.
 */
export function computeReveal(input: RevealInput): RevealResult {
  const { band, field } = input;
  const bandHeight = Math.max(0, band.bottom - band.top);

  // Reveal target: the focused field, or — when a companion is supplied and their
  // union fits the band — the union of field + companion so both stay visible.
  let targetTop = field.top;
  let targetBottom = field.bottom;
  const companion = input.companion ?? null;
  if (companion) {
    const unionTop = Math.min(field.top, companion.top);
    const unionBottom = Math.max(field.bottom, companion.bottom);
    if (unionBottom - unionTop <= bandHeight) {
      targetTop = unionTop;
      targetBottom = unionBottom;
    }
  }

  let delta = 0;
  if (targetBottom - targetTop >= bandHeight) {
    delta = targetTop - band.top; // taller than the band (or band invalid): expose the top edge
  } else if (targetBottom > band.bottom) {
    delta = targetBottom - band.bottom; // below the band: minimum downward correction
  } else if (targetTop < band.top) {
    delta = targetTop - band.top; // above the band: minimum upward correction
  }

  if (delta === 0) {
    return { scroll: input.scroll, delta: 0, changed: false };
  }

  const clamped = Math.max(0, Math.min(input.scroll + delta, input.maxScroll));
  return { scroll: clamped, delta: clamped - input.scroll, changed: clamped !== input.scroll };
}

export default computeReveal;
